import axios, { AxiosInstance } from 'axios';
import { AppConfig } from '@/constants/residents/config';
import { SecureStorage } from '@/services/shared/storage/SecureStorage';
import { AuthEndpoints } from '@/services/residents/api/endpoints/AuthEndpoints';

/**
 * Centralized refresh helper.
 * - Uses cookie-based refresh on both Axios and fetch flows.
 * - Handles different URL forms required by existing code (url or urlWithoutLogging).
 * Returns true if a new access token was stored; false otherwise.
 */
let refreshInFlight: Promise<boolean> | null = null;

export const performTokenRefresh = async (client?: AxiosInstance): Promise<boolean> => {
  try {
    // Coalesce concurrent refreshes to a single request
    if (refreshInFlight) {
      return await refreshInFlight;
    }

    const url = AuthEndpoints.refreshToken().urlWithoutLogging || AuthEndpoints.refreshToken().url;

    if (client) {
      refreshInFlight = (async () => {
        // Add timeout to prevent hanging
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Refresh timeout')), 10000); // 10 second timeout
        });
        
        const refreshPromise = client.get(url, { withCredentials: true });
        
        const response = await Promise.race([refreshPromise, timeoutPromise]);
        const data: any = response.data;
        const accessToken = data?.response?.accessToken || data?.accessToken;
        
        if (accessToken) {
          await SecureStorage.getInstance().saveToken(accessToken);
          return true;
        }
        
        console.warn('⚠️ RefreshHelper: No access token in refresh response');
        return false;
      })();
      
      const result = await refreshInFlight;
      refreshInFlight = null;
      return result;
    }

    // Fallback to axios instance for RTK baseQuery flows
    refreshInFlight = (async () => {
      // Add timeout to prevent hanging
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Refresh timeout')), 10000); // 10 second timeout
      });
      
      const refreshPromise = axios.get(
        `${AppConfig.API.BASE_URL}${url}`,
        { 
          withCredentials: true, 
          headers: { 'Content-Type': 'application/json' },
          timeout: 8000 // 8 second axios timeout as backup
        }
      );
      
      const response = await Promise.race([refreshPromise, timeoutPromise]);
      const data: any = response.data;
      const accessToken = data?.response?.accessToken || data?.accessToken;
      
      if (accessToken) {
        await SecureStorage.getInstance().saveToken(accessToken);
        return true;
      }
      
      console.warn('⚠️ RefreshHelper: No access token in refresh response');
      return false;
    })();
    
    const result = await refreshInFlight;
    refreshInFlight = null;
    return result;
  } catch (error) {
    console.error('🔴 RefreshHelper: Refresh failed:', error);
    refreshInFlight = null;
    return false;
  }
};


